import _ from "lodash";
import { toast } from "react-toastify";
import { retrieveSearchSessionMetadata, storeSearchSessionMetadata } from "../../api";
import { fetchSavedTransferSearchResults, searchRequestTransfers } from "../../api/transfers";
import { transfersErrorMsgs } from "../../errors";
import {
  SELECTED_TRANSFER_CHANGE,
  SELECTED_TRANSFER_IDLE,
  SELECTED_TRANSFER_LOADING,
  SELECTED_TRANSFER_RESET,
  SELECTED_TRANSFER_UPDATE,
  TRANSFER_FILTERS_CHANGE,
  TRANSFER_FILTERS_INIT,
  TRANSFER_FILTERS_UPDATE,
  TRANSFER_SEARCH_FORM_CHANGE,
  TRANSFER_SEARCH_FORM_RESET,
  TRANSFER_SEARCH_FORM_UPDATE,
  TRANSFER_SEARCH_RESULTS_CHANGE,
  TRANSFER_SEARCH_RESULTS_IDLE,
  TRANSFER_SEARCH_RESULTS_LOADING,
  TRANSFER_SEARCH_RESULTS_RESET,
  TRANSFER_SEARCH_RESULTS_UPDATE,
  TRANSFER_SINGLE_FILTER_INIT,
} from "./types";
import { DateTime } from "luxon";
import { MarketType } from "../../definitions";

// =================== TRANSFER SEARCH FORM =========================
export const transferSearchFormUpdate = ({ form }) => {
  return { type: TRANSFER_SEARCH_FORM_UPDATE, form };
};

export const transferSearchFormChange = (key, value) => {
  return { type: TRANSFER_SEARCH_FORM_CHANGE, key, value };
};

export const transferSearchFormReset = () => {
  return { type: TRANSFER_SEARCH_FORM_RESET };
};

// ================= TRANSFER SEARCH RESULTS ========================
export const transferSearchResultsLoading = () => {
  return { type: TRANSFER_SEARCH_RESULTS_LOADING };
};

export const transferSearchResultsIdle = () => {
  return { type: TRANSFER_SEARCH_RESULTS_IDLE };
};

export const transferSearchResultsChange = (key, value) => {
  return { type: TRANSFER_SEARCH_RESULTS_CHANGE, key, value };
};

export const transferSearchResultsUpdate = (results) => {
  return { type: TRANSFER_SEARCH_RESULTS_UPDATE, results };
};

export const transferSearchResultsReset = () => {
  return { type: TRANSFER_SEARCH_RESULTS_RESET };
};

export const transferSearchResultsFailed = () => {
  return { type: TRANSFER_SEARCH_RESULTS_CHANGE, key: "failed", value: true };
};

// ================ TRANSFER FILTER RESULTS =======================
export const transferFiltersReset = () => {
  return { type: TRANSFER_FILTERS_INIT };
};

export const transferSingleFiltersReset = (key) => {
  return { type: TRANSFER_SINGLE_FILTER_INIT, key };
};

export const transferFiltersChange = (key, value) => {
  return { type: TRANSFER_FILTERS_CHANGE, key, value };
};

export const transferFiltersUpdate = (filters) => {
  return { type: TRANSFER_FILTERS_UPDATE, filters };
};

// =================== SELECTED TRANSFER =========================
export const selectedTransferReset = () => {
  return { type: SELECTED_TRANSFER_RESET };
};

export const selectedTransferLoading = () => {
  return { type: SELECTED_TRANSFER_LOADING };
};

export const selectedTransferIdle = () => {
  return { type: SELECTED_TRANSFER_IDLE };
};

export const selectedTransferChange = ({ key, value }) => {
  return { type: SELECTED_TRANSFER_CHANGE, key, value };
};

export const selectedTransferUpdate = (data) => {
  return { type: SELECTED_TRANSFER_UPDATE, data };
};

// ===============================================================
function getTransferSearchParams({
  form,
  source_entity,
  market_type,
}: { form: any; source_entity: string; market_type: MarketType }) {
  let output = {
    origin_lat: form.origin.origin_meta.lat,
    origin_lng: form.origin.origin_meta.lng,
    origin_name: form.origin.origin_meta.label,
    origin_country_code: form.origin.origin_meta.country_code,
    destination_lat: form.destination.destination_meta.lat,
    destination_lng: form.destination.destination_meta.lng,
    destination_name: form.destination.destination_meta.label,
    destination_country_code: form.destination.destination_meta.country_code,
    inbound: DateTime.fromISO(form.inboundPickupDate).toISO({
      includeOffset: false,
    }),
    adults: form.adults,
    children: form.children,
    page_size: 100,
    origin_iata: _.get(form, "origin.origin_meta.iata", "---"), // TODO: Handle in backend when no iata in origin
    destination_iata: _.get(form, "destination.destination_meta.iata", ""),
    source_entity,
    market_type,
    route_type: form.tripMode === "poi_to_air" ? "point_to_airport" : "airport_to_point",
  };

  if (form.tripMode === "round_trip") {
    output = {
      ...output,
      outbound: DateTime.fromISO(form.outboundPickupDate).toISO({
        includeOffset: false,
      }),
    };
  }

  return output;
}

export const transferSearchRequest =
  ({ history, source_entity, market_type, setLoadingSearchBtn }) =>
  async (dispatch, getState) => {
    const state = getState();
    const search_form = state.TransfersSearchForm;
    dispatch(transferSearchResultsLoading());

    history.push("/transfers");

    const params = getTransferSearchParams({
      form: search_form,
      source_entity,
      market_type,
    });

    dispatch(transferSearchFormChange("searchPayload", params));
    const results = await searchRequestTransfers({ params });

    if (!results || !results.session_id) {
      dispatch(transferSearchResultsIdle());
      dispatch(transferSearchResultsFailed());
      if (setLoadingSearchBtn) {
        setLoadingSearchBtn(false);
      }
      toast.warning(transfersErrorMsgs.searchForm.init_search, {
        duration: 3000,
      });
      history.push("/");
      return false;
    }
    const search_session_id = _.get(results, "session_id", "");
    const transfers = _.get(results, "results", []);

    if (!transfers.length) {
      dispatch(transferSearchResultsIdle());
      if (setLoadingSearchBtn) {
        setLoadingSearchBtn(false);
      }
      toast.warning(transfersErrorMsgs.resultsList.no_results, {
        duration: 3000,
      });
      history.push("/");
      return false;
    }

    const searchSessionMetaResult = await storeSearchSessionMetadata({
      service_type: "TRF",
      session_data: {
        search_session_id,
        search_form: { ...search_form, searchPayload: params },
      },
    });

    if (
      _.isEmpty(_.get(searchSessionMetaResult, "session_data", {})) ||
      !searchSessionMetaResult.session_id
    ) {
      dispatch(transferSearchResultsIdle());
      dispatch(transferSearchResultsFailed());
      if (setLoadingSearchBtn) {
        setLoadingSearchBtn(false);
      }
      toast.warning(transfersErrorMsgs.general.no_session, {
        duration: 3000,
      });
      return;
    }

    const { session_id: page_session_id } = searchSessionMetaResult;
    const api_uid = _.get(results, "api_uid", "");

    dispatch(
      transferSearchResultsUpdate({
        transfers,
        api_uid,
        page_session_id,
        search_session_id,
      }),
    );

    if (setLoadingSearchBtn) {
      setLoadingSearchBtn(false);
    }
    dispatch(transferSearchResultsIdle());

    history.push(`/transfers/${page_session_id}`);

    return;
  };

export const fetchTrfSearchResults =
  ({ history, page_session_id }) =>
  async (dispatch) => {
    dispatch(transferSearchResultsLoading());

    const session_meta = await retrieveSearchSessionMetadata({
      page_session_id,
    });

    const search_session_id = _.get(session_meta, "session_data.data.search_session_id");

    if (!session_meta || !search_session_id) {
      toast.warning(transfersErrorMsgs.general.no_session, {
        duration: 3000,
      });
      history.push("/");
      return false;
    }

    const search_form = _.get(session_meta, "session_data.data.search_form");

    dispatch(transferSearchFormUpdate({ form: search_form }));

    const response = await fetchSavedTransferSearchResults({
      search_session_id,
    });

    if (!response) {
      dispatch(transferSearchResultsIdle());
      dispatch(transferSearchResultsFailed());
      toast.warning(transfersErrorMsgs.resultsList.no_results, {
        duration: 3000,
      });
      history.push("/");
      return false;
    }

    dispatch(
      transferSearchResultsUpdate({
        transfers: _.get(response, "results", []),
        api_uid: _.get(response, "api_uid", ""),
        page_session_id,
        search_session_id,
      }),
    );

    dispatch(transferSearchResultsIdle());

    return true;
  };
